import { WEB_CLIENT_ID } from "./google.config.web";

declare global {
  interface Window {
    google?: any;
  }
}

function ensureGsiLoaded(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("No window (SSR)"));
  }

  if (window.google?.accounts?.id) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const src = "https://accounts.google.com/gsi/client";
    const existing = document.querySelector(
      `script[src="${src}"]`,
    ) as HTMLScriptElement | null;

    if (existing) {
      // script exists; may already be loaded
      if (window.google?.accounts?.id) return resolve();

      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load Google GSI script")),
        { once: true },
      );
      return;
    }

    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Google GSI script"));
    document.head.appendChild(s);
  });
}

function ensureButtonContainer(): HTMLElement {
  let el = document.getElementById("gsi-btn");
  if (!el) {
    el = document.createElement("div");
    el.id = "gsi-btn";
    el.style.position = "fixed";
    el.style.left = "-9999px"; // offscreen
    el.style.top = "0";
    document.body.appendChild(el);
  }
  return el;
}

export default async function googleSignInWeb(): Promise<string> {
  await ensureGsiLoaded();

  return new Promise((resolve, reject) => {
    const googleAny = window.google;

    if (!googleAny?.accounts?.id) {
      reject(new Error("window.google.accounts.id is not available"));
      return;
    }

    let done = false;
    const finish = (fn: () => void) => {
      if (done) return;
      done = true;
      fn();
    };

    const timer = setTimeout(() => {
      finish(() =>
        reject(
          new Error("Google sign-in timeout: No response after 30 seconds"),
        ),
      );
    }, 30000);

    googleAny.accounts.id.initialize({
      client_id: WEB_CLIENT_ID,
      callback: (resp: any) => {
        finish(() => {
          clearTimeout(timer);

          if (resp?.error)
            return reject(new Error(`Google sign-in error: ${resp.error}`));
          if (!resp?.credential)
            return reject(new Error("No credential returned from Google"));

          resolve(resp.credential);
        });
      },
    });

    googleAny.accounts.id.prompt((notification: any) => {
      if (notification?.isNotDisplayed?.()) {
        const reason = notification.getNotDisplayedReason?.();
        finish(() => {
          clearTimeout(timer);
          reject(
            new Error(`Google One Tap not displayed: ${reason || "unknown"}`),
          );
        });
      }
    });
    const btnEl = ensureButtonContainer();
    googleAny.accounts.id.renderButton(btnEl, {
      type: "standard",
      theme: "outline",
      size: "large",
    });
  });
}
